import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, RefreshCw, Battery, Crosshair, Clock, AlertTriangle, Route as RouteIcon, ClipboardList, StopCircle, Smartphone } from "lucide-react";
import { ltApi, fmtTime, fmtHours, ago, initials, StateChip, TRACKING_META, STATE_META, personIcon, TILE_URL, TILE_ATTRIBUTION, DEFAULT_CENTER } from "./ltShared";

function FitToPoints({ points, fitKey }) {
  const map = useMap();
  const done = useRef("");
  useEffect(() => {
    if (!points.length || done.current === fitKey) return;
    done.current = fitKey;
    if (points.length === 1) map.setView(points[0], 15);
    else map.fitBounds(points, { padding: [40, 40], maxZoom: 16 });
  }, [points, fitKey, map]);
  return null;
}
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo(target, Math.max(map.getZoom(), 15), { duration: 0.6 }); }, [target, map]);
  return null;
}

export default function LiveMapTab({ businessId, isAdmin, onViewRoute }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("WORKING");
  const [focus, setFocus] = useState(null);
  const markerRefs = useRef({});

  const load = (quiet) => {
    if (!quiet) setLoading(true);
    return ltApi("/overview", { businessId })
      .then(setData)
      .catch((e) => { if (!quiet) toast.error(e.message); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [businessId]);

  const employees = useMemo(() => (data ? data.employees : []), [data]);
  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return employees.filter((e) => (stateFilter === "ALL" || e.state === stateFilter) && (!s || [e.name, e.employee_id, e.designation].some((v) => v && String(v).toLowerCase().includes(s))));
  }, [employees, q, stateFilter]);
  const onMap = visible.filter((e) => e.state === "WORKING" && e.last_point);
  const fitPts = useMemo(() => {
    const pts = onMap.map((e) => [e.last_point.latitude, e.last_point.longitude]);
    if (!pts.length && data) data.offices.forEach((o) => pts.push([o.latitude, o.longitude]));
    return pts;
  }, [onMap, data]);

  const stopTracking = async (e) => {
    if (!window.confirm(`Stop location tracking for ${e.name}? Their attendance stays open; the app stops sending locations.`)) return;
    try { await ltApi(`/sessions/${e.attendance.attendance_id}/stop-tracking`, { method: "POST", businessId }); toast.success("Tracking stopped"); load(true); }
    catch (err) { toast.error(err.message); }
  };

  const c = data ? data.counts : null;
  const cards = [
    ["WORKING", "Currently Working", c && c.working, "text-emerald-600"],
    ["TRACKING", "Location Tracking Active", c && c.tracking_active, "text-indigo-600"],
    ["PUNCHED_OUT", "Punched Out", c && c.punched_out, "text-slate-700"],
    ["ABSENT", "Absent", c && c.absent, "text-rose-600"],
    ["NOT_PUNCHED_IN", "Not Punched In", c && c.not_punched_in, "text-amber-600"],
    ["ON_LEAVE", "On Leave", c && c.on_leave, "text-violet-600"],
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map(([key, label, val, cls]) => (
          <button key={key} onClick={() => setStateFilter(key === "TRACKING" ? "WORKING" : key)} data-testid={`lt-card-${key}`}
            className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition hover:border-indigo-300 ${stateFilter === key || (key === "TRACKING" && false) ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200"}`}>
            <div className="text-xs font-medium text-slate-500">{label}</div>
            <div className={`mt-1 text-2xl font-semibold ${cls}`}>{val ?? "—"}</div>
          </button>
        ))}
      </div>

      {c && (c.mock_flags > 0 || c.geofence_exceptions > 0) && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {c.mock_flags > 0 && <span><b>{c.mock_flags}</b> session{c.mock_flags > 1 ? "s" : ""} with possible mock (fake) location — review before acting.</span>}
          {c.geofence_exceptions > 0 && <span><b>{c.geofence_exceptions}</b> punch-in{c.geofence_exceptions > 1 ? "s" : ""} outside the office geo-fence.</span>}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        <div className="flex max-h-[620px] flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="space-y-2 border-b border-slate-100 p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee…" className="h-9 w-full rounded-lg border border-slate-200 pl-8 pr-3 text-sm focus:border-indigo-400 focus:outline-none" />
            </div>
            <div className="flex items-center gap-2">
              <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="h-8 flex-1 rounded-lg border border-slate-200 px-2 text-xs">
                <option value="ALL">All staff</option>
                {Object.entries(STATE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
              <button onClick={() => load()} title="Refresh" className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && !data ? <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
              : visible.length === 0 ? <div className="p-6 text-center text-sm text-slate-400">No staff in this view.</div>
              : visible.map((e) => (
                <button key={e.user_id} onClick={() => { if (e.last_point) { setFocus([e.last_point.latitude, e.last_point.longitude]); setTimeout(() => markerRefs.current[e.user_id]?.openPopup(), 650); } }}
                  className="flex w-full items-center gap-3 border-b border-slate-50 px-3 py-2.5 text-left hover:bg-slate-50">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: (STATE_META[e.state] || {}).dot || "#64748b" }}>{initials(e.name)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-900">{e.name}</span>
                    <span className={`block truncate text-xs ${TRACKING_META[e.tracking]?.cls || "text-slate-500"}`}>
                      {e.state === "WORKING" ? `${TRACKING_META[e.tracking]?.label} · ${e.last_point ? ago(e.last_point.captured_at) : "no location yet"}` : e.designation || e.employee_id || ""}
                    </span>
                  </span>
                  <StateChip state={e.state} />
                </button>
              ))}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" style={{ minHeight: 520 }}>
          <MapContainer center={DEFAULT_CENTER} zoom={5} style={{ height: "100%", minHeight: 520, width: "100%" }} scrollWheelZoom>
            <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
            <FitToPoints points={fitPts} fitKey={`${businessId}-${stateFilter}-${fitPts.length}`} />
            <FlyTo target={focus} />
            {data && data.offices.map((o, i) => (
              <Circle key={i} center={[o.latitude, o.longitude]} radius={o.radius_meters} pathOptions={{ color: "#6366f1", weight: 1.5, fillOpacity: 0.08 }}>
                <Popup><b>{o.name}</b><br />Geo-fence radius {o.radius_meters} m</Popup>
              </Circle>
            ))}
            {onMap.map((e) => {
              const p = e.last_point;
              const color = e.tracking === "active" ? "#16a34a" : e.tracking === "stale" ? "#d97706" : "#64748b";
              return (
                <Marker key={e.user_id} position={[p.latitude, p.longitude]} icon={personIcon(e.name, color, { pulse: e.tracking === "active" })} ref={(r) => { if (r) markerRefs.current[e.user_id] = r; }}>
                  <Popup minWidth={250}>
                    <div className="space-y-2 text-[13px]">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{e.name}</div>
                        <div className="text-xs text-slate-500">{[e.designation, e.employee_id].filter(Boolean).join(" · ")}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                        <span>Status</span><span className="font-medium text-emerald-700">Working</span>
                        <span>Punch In</span><span className="font-medium text-slate-800">{fmtTime(e.attendance?.punch_in_time)}</span>
                        <span>Last Location</span><span className="font-medium text-slate-800">{fmtTime(p.captured_at)}</span>
                        <span>Last Update</span><span className={`font-medium ${TRACKING_META[e.tracking]?.cls}`}>{ago(p.captured_at)}</span>
                        <span className="inline-flex items-center gap-1"><Battery className="h-3 w-3" />Battery</span><span className="font-medium text-slate-800">{p.battery_level ?? "—"}{p.battery_level != null ? "%" : ""}</span>
                        <span className="inline-flex items-center gap-1"><Crosshair className="h-3 w-3" />Accuracy</span><span className="font-medium text-slate-800">{p.accuracy != null ? `${Math.round(p.accuracy)} m` : "—"}</span>
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />Worked</span><span className="font-medium text-slate-800">{fmtHours((Date.now() - new Date(e.attendance?.punch_in_time).getTime()) / 3600000)}</span>
                        {e.device && <><span className="inline-flex items-center gap-1"><Smartphone className="h-3 w-3" />Device</span><span className="truncate font-medium text-slate-800">{e.device.model || "—"}</span></>}
                      </div>
                      {(p.is_mock || e.attendance?.geofence_exception) && (
                        <div className="rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                          {p.is_mock && "Possible mock location reported by the device. "}
                          {e.attendance?.geofence_exception && "Punched in outside the geo-fence."}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <button onClick={() => onViewRoute(e.user_id)} className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700"><RouteIcon className="h-3 w-3" /> View Route</button>
                        <Link to="/attendance/records" className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"><ClipboardList className="h-3 w-3" /> View Attendance</Link>
                        {isAdmin && e.tracking !== "stopped" && <button onClick={() => stopTracking(e)} className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"><StopCircle className="h-3 w-3" /> Stop tracking</button>}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
          {data && onMap.length === 0 && (
            <div className="pointer-events-none absolute inset-x-0 top-3 z-[500] mx-auto w-fit rounded-full bg-white/95 px-4 py-1.5 text-xs font-medium text-slate-600 shadow">
              No working staff with a live location right now.
            </div>
          )}
          <div className="pointer-events-none absolute bottom-3 left-3 z-[500] flex gap-3 rounded-lg bg-white/95 px-3 py-1.5 text-[11px] text-slate-600 shadow">
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />Live</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-600" />No update &gt; {data?.settings?.stale_after_minutes ?? "—"} min</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border border-indigo-500 bg-indigo-100" />Office geo-fence</span>
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-400">Locations are shown only for staff with an active work session (Punch In → Punch Out). Updates every minute.</p>
    </div>
  );
}
